import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../components/Button.jsx";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@shadcn-in-nx/ui/dialog";
import { Input } from "@shadcn-in-nx/ui/input";
import { Label } from "@shadcn-in-nx/ui/label";
import { Plus, Save } from "lucide-react";
import { useBluelagoon } from "@bluelagoon";
import { useNavigate } from "react-router-dom";
import Message from "../../components/Message.jsx";
import { RadioGroup, RadioGroupItem } from "@shadcn-in-nx/ui/radio-group";
import Icon from "../../components/Icon";
import { track } from "@crm/analytics";
import { useAuth } from "../../context/AuthContext";
import { useWorkflowLimits } from "../../components/useWorkflowLimits";
import { toast } from "sonner";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@shadcn-in-nx/ui/alert-dialog";

export function DialogNewWorkflow({ children, appId, appType, queryString }) {
    const { t } = useTranslation();
    const [name, setName] = React.useState("");
    const [type, setType] = React.useState(null);
    const navigate = useNavigate();
    const [nameError, setNameError] = React.useState(null);
    const { trialStatus, isInTrial, interfaceStyle } = useAuth();
    const [showLimitModal, setShowLimitModal] = React.useState(false);
    const {
        activeWorkflowCount,
        workflowLimit,
        currentPlan,
        isAtLimit,
        canCreateWorkflow,
        remainingWorkflows,
        loading: limitsLoading,
        error: limitsError,
        refreshCount,
    } = useWorkflowLimits();

    const OPTIONS = React.useMemo(
        () => [
            ...(appType !== "AUTOMATION"
                ? [
                      {
                          id: "r1",
                          value: "TOOL",
                          icon: "Tool",
                          title: t("dialogNewWorkflow.types.tool"),
                          description: t(
                              "dialogNewWorkflow.types.toolDescription"
                          ),
                      },
                  ]
                : []),
            {
                id: "r2",
                value: "AUTOMATION",
                icon: "AUTOMATION",
                title: t("dialogNewWorkflow.types.automation"),
                description: t("dialogNewWorkflow.types.automationDescription"),
            },
        ],
        [appType, t]
    );

    const { call, error, isLoading } = useBluelagoon({
        method: "createWorkflow",
    });

    // Check if trial has expired
    const isTrialExpired = isInTrial && trialStatus && !trialStatus.isActive;

    const handleCreateWorkflow = (e) => {
        e.preventDefault();
        if (!name) {
            setNameError(t("dialogNewWorkflow.nameRequired"));
            return;
        }

        // Check workflow limits before creating
        if (!canCreateWorkflow) {
            toast.error(
                t("dialogNewWorkflow.workflowLimitReached", {
                    limit: workflowLimit,
                    current: activeWorkflowCount,
                    plan: currentPlan,
                })
            );
            setShowLimitModal(true);
            return;
        }

        setNameError(null);
        call({ name, type, appId })
            .then((res) => {
                console.log(res);

                track("New Automation", { name, type });

                // Refresh the workflow count after successful creation
                refreshCount();

                // Redirect based on interface style
                const workflowId = res?.workflowId;

                navigate(`/chat/${workflowId}?${queryString ?? ""}`);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const handleClick = () => {
        if (isTrialExpired) {
            navigate("/billing");
            return;
        }

        // Check workflow limits
        if (isAtLimit) {
            toast.error(
                t("dialogNewWorkflow.workflowLimitReached", {
                    limit: workflowLimit,
                    current: activeWorkflowCount,
                    plan: currentPlan,
                })
            );
            setShowLimitModal(true);
            return;
        }

        // If trial is not expired and not at limit, the dialog will open normally
    };

    const handleUpgrade = () => {
        setShowLimitModal(false);
        navigate("/billing");
    };

    // If trial is expired, render a clickable element that redirects to billing
    if (isTrialExpired) {
        return (
            <div onClick={handleClick} className="cursor-pointer">
                {children || (
                    <div className="text-center p-4 border border-dashed border-gray-300 bg-gray-50 hover:shadow transition-shadow rounded-md aspect-video flex flex-col items-center justify-center space-y-2 gap-1">
                        <Plus
                            size={40}
                            className="text-gray-400"
                            strokeWidth={2}
                            aria-hidden="true"
                        />
                        <p className="font-medium md:text-base text-sm truncate">
                            {t("dialogNewWorkflow.newAutomationOrApp")}
                        </p>
                    </div>
                )}
            </div>
        );
    }

    return (
        <>
            <Dialog>
                <DialogTrigger asChild>
                    {children || (
                        <div
                            className="cursor-pointer text-center p-4 border border-dashed border-gray-300 bg-gray-50 hover:shadow transition-shadow rounded-md aspect-video flex flex-col items-center justify-center space-y-2 gap-1"
                            onClick={handleClick}
                        >
                            <Plus
                                size={40}
                                className="text-gray-400"
                                strokeWidth={2}
                                aria-hidden="true"
                            />
                            <p className="font-medium md:text-base text-sm truncate">
                                {t("dialogNewWorkflow.newAutomationOrApp")}
                            </p>
                            {isAtLimit && (
                                <p className="text-xs text-red-500 mt-1 text-center">
                                    {t("dialogNewWorkflow.limitReached")}
                                </p>
                            )}
                        </div>
                    )}
                </DialogTrigger>
                {isAtLimit && (
                    <p className="text-xs text-red-500 mt-1 text-center">
                        {t("dialogNewWorkflow.limitReached")}
                    </p>
                )}
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>
                            {t("dialogNewWorkflow.createNewAutomationOrApp")}
                        </DialogTitle>
                        <DialogDescription>
                            {t("dialogNewWorkflow.createNewDescription")}
                        </DialogDescription>
                        {!limitsLoading && (
                            <div className="text-sm text-muted-foreground">
                                {t("dialogNewWorkflow.workflowUsage", {
                                    current: activeWorkflowCount,
                                    limit:
                                        workflowLimit === Infinity
                                            ? "∞"
                                            : workflowLimit,
                                    plan: currentPlan,
                                })}
                            </div>
                        )}
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>
                                {t("dialogNewWorkflow.automationOrAppName")}
                            </Label>
                            <Input
                                id="name"
                                defaultValue=""
                                placeholder={t(
                                    "dialogNewWorkflow.namePlaceholder"
                                )}
                                value={name}
                                required
                                onChange={(e) => setName(e.target.value)}
                                data-guided="workflow-name-input"
                            />
                            {nameError && (
                                <p className="text-red-500 text-sm mt-1">
                                    {nameError}
                                </p>
                            )}
                        </div>
                        {/* <div className="space-y-2">
                        <Label>Type</Label>
                        <RadioGroup
                            className="gap-2"
                            defaultValue="AUTOMATION"
                            onValueChange={setType}
                        >
                            {OPTIONS.map((item) => (
                                <div
                                    key={item.id}
                                    className="relative flex w-full items-start gap-2 rounded-lg border border-input p-4 shadow-sm shadow-black/5 has-[[data-state=checked]]:border-ring"
                                >
                                    <RadioGroupItem
                                        id={item.id}
                                        value={item.value}
                                        className="order-1 after:absolute after:inset-0"
                                    />
                                    <div className="flex grow items-start gap-3">
                                        <Icon
                                            name={item.icon}
                                            className="opacity-50"
                                        />
                                        <div className="grid grow gap-2">
                                            <Label htmlFor={item.id}>
                                                {item.title}
                                            </Label>
                                            <p className="text-xs text-muted-foreground">
                                                {item.description}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </RadioGroup>
                    </div> */}
                    </div>
                    {error && error.message ? (
                        <Message className="text-red-500" error>
                            {error?.message}
                        </Message>
                    ) : null}
                    <DialogFooter className="gap-2">
                        <DialogClose asChild>
                            <Button type="button" variant="secondary">
                                {t("dialogNewWorkflow.cancel")}
                            </Button>
                        </DialogClose>
                        <Button
                            type="button"
                            onClick={handleCreateWorkflow}
                            disabled={isLoading || isAtLimit}
                            isLoading={isLoading ? "true" : undefined}
                            data-guided="create-workflow"
                        >
                            <Save />
                            {t("dialogNewWorkflow.save")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Workflow Limit Modal */}
            <AlertDialog open={showLimitModal} onOpenChange={setShowLimitModal}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {t("dialogNewWorkflow.limitModal.title")}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {t("dialogNewWorkflow.limitModal.description", {
                                limit: workflowLimit,
                                current: activeWorkflowCount,
                                plan: currentPlan,
                            })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>
                            {t("dialogNewWorkflow.limitModal.cancel")}
                        </AlertDialogCancel>
                        <AlertDialogAction onClick={handleUpgrade}>
                            {t("dialogNewWorkflow.limitModal.upgrade")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
